import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, CalendarDays, CheckCircle2, GraduationCap } from 'lucide-react';
import { formatDay } from '@/pages/coordinator/AssignMentorDialog';
import CareerMapping, { CAREER_FIELDS } from '@/components/internships/CareerMapping';

export const GRADES = [9, 10, 11, 12];
export const FORMATS = [
  { value: 'on_site', label: 'On-site' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
];
export const EMPTY_OFFER = {
  company_name: '', industry: '', website: '', contact_name: '', contact_email: '', contact_phone: '',
  title: '', description: '', student_activities: '', learning_outcomes: '',
  eligible_grades: [], prerequisites: '', format: '', start_date: '', end_date: '',
  daily_start_time: '', daily_end_time: '', positions: '', workplace_address: '',
  supervisor_name: '', supervisor_email: '', supervisor_phone: '', additional_instructions: '',
  ...CAREER_FIELDS,
};

export function offerToForm(o) {
  const out = {};
  Object.keys(EMPTY_OFFER).forEach(k => { out[k] = o?.[k] ?? EMPTY_OFFER[k]; });
  out.eligible_grades = o?.eligible_grades || [];
  out.related_career_slugs = o?.related_career_slugs || [];
  out.career_request_name = o?.career_request?.name || '';
  out.career_request_description = o?.career_request?.description || '';
  out.positions = o?.positions ?? '';
  return out;
}

const inputCls = 'mt-1.5 rounded-xl h-11 border-gray-200';
const areaCls = 'mt-1.5 rounded-xl border-gray-200 min-h-[80px] resize-none';

const Field = ({ label, required, hint, children }) => (
  <div>
    <Label className="text-[#1F2937] font-medium text-sm">
      {label}{required && <span className="text-[#F97D6A]"> *</span>}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-[#9CA3AF] mt-1">{hint}</p>}
  </div>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 pt-2">
    <Icon className="h-4 w-4 text-[#F97D6A]" />
    <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit']">{children}</h2>
  </div>
);

/** The shared internship offer fields, reused by partners and company representatives. */
export default function OfferFields({ form, set, careers, program, lockCompanyName, careerStatus }) {
  const toggleGrade = (g) => set('eligible_grades',
    form.eligible_grades.includes(g) ? form.eligible_grades.filter(x => x !== g) : [...form.eligible_grades, g]);
  const needsAddress = form.format === 'on_site' || form.format === 'hybrid';

  return (
    <>
      <SectionTitle icon={Building2}>Company</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Company name" required hint={lockCompanyName ? 'From your company profile' : null}>
          <Input value={form.company_name} disabled={lockCompanyName} onChange={e => set('company_name', e.target.value)} className={`${inputCls} ${lockCompanyName ? 'bg-[#F9FAFB]' : ''}`} data-testid="f-company-name" />
        </Field>
        <Field label="Industry" required>
          <Input value={form.industry} onChange={e => set('industry', e.target.value)} placeholder="e.g. Software, Healthcare" className={inputCls} data-testid="f-industry" />
        </Field>
        <Field label="Website">
          <Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://" className={inputCls} data-testid="f-website" />
        </Field>
        <Field label="Contact name" required>
          <Input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} className={inputCls} data-testid="f-contact-name" />
        </Field>
        <Field label="Contact email" required hint="The school replies to this address">
          <Input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} className={inputCls} data-testid="f-contact-email" />
        </Field>
        <Field label="Contact phone">
          <Input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} className={inputCls} data-testid="f-contact-phone" />
        </Field>
      </div>

      <SectionTitle icon={GraduationCap}>The internship</SectionTitle>
      <Field label="Internship title" required>
        <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Product Design Taster Week" className={inputCls} data-testid="f-title" />
      </Field>
      <CareerMapping form={form} set={set} careers={careers} status={careerStatus} />
      <Field label="Description" required>
        <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="What the internship is about" className={areaCls} data-testid="f-description" />
      </Field>
      <Field label="What students will do" required>
        <Textarea value={form.student_activities} onChange={e => set('student_activities', e.target.value)} placeholder="Day-to-day activities, projects, shadowing" className={areaCls} data-testid="f-activities" />
      </Field>
      <Field label="Learning outcomes" required>
        <Textarea value={form.learning_outcomes} onChange={e => set('learning_outcomes', e.target.value)} placeholder="What students take away from the week" className={areaCls} data-testid="f-outcomes" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Eligible grades" required hint={program ? `This programme allows grades ${program.eligible_grades?.join(', ')}` : null}>
          <div className="flex gap-2 mt-1.5">
            {GRADES.map(g => {
              const allowed = !program || program.eligible_grades?.includes(g);
              return (
                <button key={g} type="button" disabled={!allowed} onClick={() => toggleGrade(g)}
                  className={`h-11 flex-1 rounded-xl text-sm font-semibold border transition-colors ${
                    form.eligible_grades.includes(g) ? 'bg-[#FFF0ED] border-[#F97D6A] text-[#F06651]'
                      : allowed ? 'bg-white border-gray-200 text-[#6B7280] hover:border-[#F97D6A]'
                        : 'bg-[#F3F4F6] border-transparent text-[#C8CDD6] cursor-not-allowed'}`}
                  data-testid={`f-grade-${g}`}>
                  {g}
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Prerequisites">
          <Input value={form.prerequisites} onChange={e => set('prerequisites', e.target.value)} placeholder="e.g. basic coding, own laptop" className={inputCls} data-testid="f-prerequisites" />
        </Field>
      </div>

      <SectionTitle icon={CalendarDays}>Dates and logistics</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Format" required>
          <Select value={form.format} onValueChange={v => set('format', v)}>
            <SelectTrigger className={inputCls} data-testid="f-format"><SelectValue placeholder="On-site, Remote or Hybrid" /></SelectTrigger>
            <SelectContent>{FORMATS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Positions available" required>
          <Input type="number" min="1" value={form.positions} onChange={e => set('positions', e.target.value)} className={inputCls} data-testid="f-positions" />
        </Field>
        <Field label="Internship start" required hint={program ? `Within ${formatDay(program.start_date)} – ${formatDay(program.end_date)}` : null}>
          <Input type="date" min={program?.start_date} max={program?.end_date} value={form.start_date} onChange={e => set('start_date', e.target.value)} className={inputCls} data-testid="f-start-date" />
        </Field>
        <Field label="Internship end" required hint="Up to 7 days in total">
          <Input type="date" min={form.start_date || program?.start_date} max={program?.end_date} value={form.end_date} onChange={e => set('end_date', e.target.value)} className={inputCls} data-testid="f-end-date" />
        </Field>
        <Field label="Daily start time" required>
          <Input type="time" value={form.daily_start_time} onChange={e => set('daily_start_time', e.target.value)} className={inputCls} data-testid="f-daily-start" />
        </Field>
        <Field label="Daily finish time" required>
          <Input type="time" value={form.daily_end_time} onChange={e => set('daily_end_time', e.target.value)} className={inputCls} data-testid="f-daily-end" />
        </Field>
      </div>
      {needsAddress && (
        <Field label="Workplace address" required>
          <Textarea value={form.workplace_address} onChange={e => set('workplace_address', e.target.value)} placeholder="Where students report each day" className={areaCls} data-testid="f-address" />
        </Field>
      )}

      <SectionTitle icon={CheckCircle2}>Supervision</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Supervisor name" required>
          <Input value={form.supervisor_name} onChange={e => set('supervisor_name', e.target.value)} className={inputCls} data-testid="f-supervisor-name" />
        </Field>
        <Field label="Supervisor email" required>
          <Input type="email" value={form.supervisor_email} onChange={e => set('supervisor_email', e.target.value)} className={inputCls} data-testid="f-supervisor-email" />
        </Field>
        <Field label="Supervisor phone">
          <Input value={form.supervisor_phone} onChange={e => set('supervisor_phone', e.target.value)} className={inputCls} data-testid="f-supervisor-phone" />
        </Field>
      </div>
      <Field label="Additional instructions or requirements">
        <Textarea value={form.additional_instructions} onChange={e => set('additional_instructions', e.target.value)} placeholder="Dress code, ID, safety briefing, anything else the school should know" className={areaCls} data-testid="f-instructions" />
      </Field>
    </>
  );
}
